import React, { useMemo, useState } from "react";
import { Search, SlidersHorizontal, LayoutList, LayoutGrid, Plus } from "lucide-react";
import { TicketProps } from "../../ticket/types";
import { formatDateLong2 } from "../../../../utils/function";
import { RaiseTicketModal } from "../../../../components/crm/customer/ticket-pop-up";

const TICKET_STATUS_STYLES: Record<string, string> = {
  open: "bg-yellow-50 dark:bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/40",
  "to do": "bg-yellow-50 dark:bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/40",
  "in progress": "bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/40",
  closed: "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700",
  resolved: "bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/40",
  pending: "bg-purple-50 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-500/40",
};

const statusDotClass: Record<string, string> = {
  open: "bg-yellow-500",
  "to do": "bg-yellow-500",
  "in progress": "bg-blue-500",
  closed: "bg-gray-400",
  resolved: "bg-green-500",
  pending: "bg-purple-500",
};

const priorityColor: Record<string, string> = {
  critical: "text-red-600",
  high: "text-orange-500",
  medium: "text-yellow-600",
  low: "text-green-600",
};

const TicketStatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  const key = (status || "open").toLowerCase().replace("_", " ");
  const cls = TICKET_STATUS_STYLES[key] || TICKET_STATUS_STYLES.open;
  const dot = statusDotClass[key] || "bg-gray-400";
  const label = (status || "Open").replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
};

// ── TicketsTab ────────────────────────────────────────────────────────────────

interface TicketsTabProps {
  customer?: {
    first_name?: string;
    last_name?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
  } | null;
  tickets: TicketProps[] | null | undefined;
  loading: boolean;
  onRefresh?: () => void;
}

const PAGE_SIZE = 10;

const TicketsTab: React.FC<TicketsTabProps> = ({
  customer,
  tickets = [],
  loading,
  onRefresh,
}) => {
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [page, setPage] = useState(1);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<TicketProps | null>(null);

  // Safe filtering
  const filtered = useMemo(() => {
    const ticketList = Array.isArray(tickets) ? tickets : [];
    if (!search.trim()) return ticketList;

    const q = search.toLowerCase();
    return ticketList.filter((t) => {
      return (
        (t.issue_type || "").toLowerCase().includes(q) ||
        (t.issue_description || "").toLowerCase().includes(q) ||
        (t.ticket_id || "").toLowerCase().includes(q)
      );
    });
  }, [tickets, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const fullName = customer
    ? `${customer.first_name || customer.firstName || ""} ${customer.last_name || customer.lastName || ""}`.trim()
    : "";

  const handleOpenTicket = (ticket?: TicketProps) => {
    setSelectedTicket(ticket || null);
    setIsTicketModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsTicketModalOpen(false);
    setSelectedTicket(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => handleOpenTicket()}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3.5 py-2 rounded-lg transition-colors"
        >
          <Plus size={14} />
          New Ticket
        </button>

        <div className="relative flex-1 min-w-[160px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search Tickets"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500/30"
          />
        </div>

        <button className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-medium px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
          <SlidersHorizontal size={13} />
          Add Filters
        </button>

        <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode("list")}
            className={`px-2.5 py-2 transition-colors ${viewMode === "list" ? "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" : "text-[#7B7B7B] font-semibold dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"}`}
          >
            <LayoutList size={15} />
          </button>
          <button
            onClick={() => setViewMode("grid")}
            className={`px-2.5 py-2 transition-colors ${viewMode === "grid" ? "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" : "text-[#7B7B7B] font-semibold dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"}`}
          >
            <LayoutGrid size={15} />
          </button>
        </div>
      </div>

      {/* Table / Content */}
      {paged.length === 0 ? (
        <div className="text-center py-12 text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm">
          {search ? "No tickets match your search." : "No tickets found for this contact."}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-800">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Issue ↑</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Created On</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Status ↑</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Priority ↑</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide hidden md:table-cell">Description</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide hidden lg:table-cell">Assigned To</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => handleOpenTicket(t)}
                  className="border-b border-gray-50 dark:border-gray-800 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/10 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                      {t.issue_type || "Support Ticket"}
                    </p>
                    <span className="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5">
                      {t.ticket_id ? `TicketID #${t.ticket_id}` : `#${t.id?.slice(0, 6)}`}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {t.created_at ? formatDateLong2(t.created_at) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <TicketStatusBadge status={t.status} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-medium capitalize ${priorityColor[t.priority?.toLowerCase() || ""] || "text-gray-600 dark:text-gray-400"}`}>
                      {t.priority || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell max-w-[180px] truncate">
                    {t.issue_description || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                    {t.assigned_to_user_id || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-2.5 py-1.5 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-8 h-8 rounded border text-xs font-medium transition-colors ${page === p ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
              >
                {p}
              </button>
            ))}
            {totalPages > 5 && <span className="px-1">…</span>}
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-2.5 py-1.5 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span>Items Per Page</span>
            <select className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded px-2 py-1 text-xs">
              <option>10</option>
              <option>25</option>
              <option>50</option>
            </select>
            <span>of {filtered.length.toLocaleString()}</span>
          </div>
        </div>
      )}

      <RaiseTicketModal
        isOpen={isTicketModalOpen}
        onClose={handleCloseModal}
        conversationId={null}
        prefillCustomerData={{
          name: fullName,
          phone: customer?.phone || "",
          email: customer?.email || "",
        }}
        existingTicket={selectedTicket || undefined}
      />
    </div>
  );
};

export default TicketsTab;